import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from 'api/Api';
import { invalidateAuthTags } from 'api/AuthApi';

export interface HipaaActiveVersion {
  version: string;
  title: string;
  authorizationText: string;
  createdAt: string;
}

export interface AcceptHipaaPayload {
  authorizationVersion: string;
  authorizationTitle?: string;
  authorizationCreatedAt?: string;
  signerName: string;
  signedByUserId?: string;
  signedByPatientProfileId?: string;
  clientSignedAt: string;
  clientTimeZone?: string;
  userAgent?: string;
  signature: string;
}

export const buildAcceptHipaaRequestBody = (payload: AcceptHipaaPayload): Partial<AcceptHipaaPayload> => {
  const requestBody: Partial<AcceptHipaaPayload> = {
    authorizationVersion: payload.authorizationVersion,
    signerName: payload.signerName,
    clientSignedAt: payload.clientSignedAt,
    signature: payload.signature
  };

  if (payload.authorizationTitle) {
    requestBody.authorizationTitle = payload.authorizationTitle;
  }

  if (payload.authorizationCreatedAt) {
    requestBody.authorizationCreatedAt = payload.authorizationCreatedAt;
  }

  if (payload.signedByUserId) {
    requestBody.signedByUserId = payload.signedByUserId;
  }

  if (payload.signedByPatientProfileId) {
    requestBody.signedByPatientProfileId = payload.signedByPatientProfileId;
  }

  if (payload.clientTimeZone) {
    requestBody.clientTimeZone = payload.clientTimeZone;
  }

  if (payload.userAgent) {
    requestBody.userAgent = payload.userAgent;
  }

  return requestBody;
};

export const hipaaApi = createApi({
  baseQuery: baseQueryWithReAuth,
  reducerPath: 'hipaaApi',
  endpoints: (build) => ({
    acceptHipaa: build.mutation<boolean, AcceptHipaaPayload>({
      query: (payload) => ({
        url: '/hipaa/accept',
        method: 'POST',
        body: buildAcceptHipaaRequestBody(payload)
      }),
      transformResponse: (response: { success: boolean }) => response.success,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    revokeHipaa: build.mutation<boolean, void>({
      query: () => ({
        url: '/hipaa/revoke',
        method: 'POST'
      }),
      transformResponse: (response: { success: boolean }) => response.success,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    getActiveHipaaVersion: build.query<HipaaActiveVersion, void>({
      query: () => ({
        url: '/hipaa/active-version',
        method: 'GET'
      }),
      transformResponse: (response: { data: HipaaActiveVersion }) => response.data
    })
  })
});

export const { useAcceptHipaaMutation, useRevokeHipaaMutation, useGetActiveHipaaVersionQuery } = hipaaApi;
